import {
  IfilterProps, InotifStnUser, TnotifStnUserRequest,
  validateData
} from '@biz-nest/core-client';
import { lastValueFrom } from 'rxjs';
import { NotificationClient } from '../notif-client';

/**
 * Represents a user's notification settings in the system.
 * This class provides methods to manage notification preferences for users,
 * including email, SMS, push, and web notifications.
 *
 * @class NotifStnUser
 * @example
 * // Get notification settings for a user
 * const settings = await NotifStnUser.getOne('user123');
 *
 * // Update notification preferences
 * await settings.update({
 *   disableAll: false,
 *   channels: ['email', 'push'],
 *   allow: []
 * });
 */
export class NotifStnUser {
  /** Base URL for notification settings endpoints */
  static readonly baseUrl = '/notification-setting-user';

  /** Unique identifier for the notification settings */
  readonly _id: InotifStnUser['_id'];

  /** ID of the user these settings belong to */
  readonly authUser: InotifStnUser['authUser'];

  /** Optional target IDs if these settings are target-specific */
  readonly targetId?: InotifStnUser['targetId'];

  /** Optional company ID if these settings are company-specific */
  readonly company?: InotifStnUser['company'];

  /** Whether all notifications are disabled */
  readonly disableAll?: InotifStnUser['disableAll'];

  /** Array of allowed notification settings */
  readonly allow: InotifStnUser['allow'];

  /** Notification frequency in minutes */
  readonly frequency?: InotifStnUser['frequency'];

  /** Array of enabled notification channels */
  readonly channels?: InotifStnUser['channels'];

  /** User's timezone for time-sensitive notifications */
  readonly timezone?: InotifStnUser['timezone'];

  /** User's preferred language for notifications */
  readonly language?: InotifStnUser['language'];

  /** Whether these notification settings are active */
  readonly isActive?: InotifStnUser['isActive'];

  /**
   * Creates a new NotifStnUser instance with validated data
   * @param {InotifStnUser} notifStnUser - The notification settings data
   * @throws {Error} If the notification settings data is invalid
   */
  constructor(notifStnUser: InotifStnUser) {
    const validChannels = ['email', 'sms', 'push', 'web'];

    validateData(notifStnUser, {
      _id: {
        validate: (v) =>
          typeof v === 'string' &&
          v.trim().length > 0,
        message: 'Unique identifier must be a non-empty string'
      },
      company: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'string' && v.trim().length > 0),
        message: 'Company ID must be a non-empty string if provided'
      },
      disableAll: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'boolean',
        message: 'Disable all must be a boolean if provided'
      },
      allow: {
        validate: (v) =>
          Array.isArray(v) &&
          v.every(setting =>
            typeof setting === 'object' &&
            setting !== null),
        message: 'Allow must be an array of notification settings'
      },
      frequency: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (typeof v === 'number' && v >= 0),
        message: 'Frequency must be a non-negative number if provided'
      },
      channels: {
        required: false,
        validate: (v) =>
          v === undefined ||
          (Array.isArray(v) &&
            v.every(channel =>
              typeof channel === 'string' &&
            validChannels.includes(channel))),
        message: `Channels must be an array of valid notification channels: ${validChannels.join(', ')}`
      },
      timezone: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'string',
        message: 'Timezone must be a string if provided'
      },
      language: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'string',
        message: 'Language must be a string if provided'
      },
      isActive: {
        required: false,
        validate: (v) =>
          v === undefined ||
          typeof v === 'boolean',
        message: 'Is active must be a boolean if provided'
      }
    });

    this._id = notifStnUser._id;
    this.authUser = notifStnUser.authUser;
    this.company = notifStnUser.company;
    this.disableAll = notifStnUser.disableAll;
    this.allow = notifStnUser.allow;
    this.frequency = notifStnUser.frequency;
    this.channels = notifStnUser.channels;
    this.timezone = notifStnUser.timezone;
    this.language = notifStnUser.language;
    this.isActive = notifStnUser.isActive;
    this.targetId = notifStnUser.targetId;
  }

  /**
   * Retrieves a paginated list of notification settings for users
   * @param {number} offset - The number of items to skip before starting to collect the result set
   * @param {number} limit - The maximum number of items to return
   * @returns {Promise<NotifStnUser[]>} Array of notification settings
   * @throws {Error} If the API request fails
   * @example
   * // Get first 10 notification settings
   * const settings = await NotifStnUser.getAll(0, 10);
   */
  static async getAll(offset: number, limit: number): Promise<NotifStnUser[]> {
    const query = new URLSearchParams({
      offset: offset.toString(),
      limit: limit.toString()
    }).toString();
    const observer$ = NotificationClient.ehttp
      .makeGet<InotifStnUser[]>(`${NotifStnUser.baseUrl}?${query}`);
    const response = await lastValueFrom(observer$);

    return response.map(item => new NotifStnUser(item));
  }

  /**
   * Retrieves notification settings based on filter criteria
   * @param {IfilterProps} filter - The filter criteria to apply
   * @returns {Promise<NotifStnUser[]>} Filtered notification settings
   * @throws {Error} If the API request fails
   * @example
   * // Get notification settings for active users
   * const activeSettings = await NotifStnUser.filterAll({
   *   conditions: { isActive: true }
   * });
   */
  static async filterAll(filter: IfilterProps): Promise<NotifStnUser[]> {
    const observer$ = NotificationClient.ehttp
      .makePost<InotifStnUser[]>(`${NotifStnUser.baseUrl}/filter`, { filter });
    const response = await lastValueFrom(observer$);

    return response.map(item => new NotifStnUser(item));
  }

  /**
   * Retrieves notification settings for a specific user
   * @param {string} resourceId - The ID of the notification settings to retrieve
   * @returns {Promise<NotifStnUser>} The requested notification settings
   * @throws {Error} If the settings are not found or request fails
   * @example
   * const userSettings = await NotifStnUser.getOne('user123');
   */
  static async getOne(resourceId: string): Promise<NotifStnUser> {
    const observer$ = NotificationClient.ehttp
      .makeGet<InotifStnUser>(`${NotifStnUser.baseUrl}/${resourceId}`);
    const response = await lastValueFrom(observer$);

    return new NotifStnUser(response);
  }

  /**
   * Creates new notification settings for a user
   * @param {TnotifStnUserRequest<true>} payload - The notification settings to create
   * @returns {Promise<NotifStnUser>} The created notification settings
   * @throws {Error} If creation fails or validation fails
   * @example
   * const newSettings = await NotifStnUser.add({
   *   user: 'user123',
   *   allow: [],
   *   channels: ['email', 'push'],
   *   isActive: true
   * });
   */
  static async add(payload: TnotifStnUserRequest<true>): Promise<NotifStnUser> {
    const observer$ = NotificationClient.ehttp
      .makePost<InotifStnUser>(`${NotifStnUser.baseUrl}`, payload);

    return new NotifStnUser(await lastValueFrom(observer$));
  }

  /**
   * Updates the current notification settings
   * @param {TnotifStnUserRequest} payload - The fields to update
   * @returns {Promise<NotifStnUser>} The updated notification settings
   * @throws {Error} If the update fails or settings don't exist
   * @example
   * // Disable email notifications
   * await userSettings.update({
   *   channels: ['push', 'sms']
   * });
   */
  async update(payload: TnotifStnUserRequest): Promise<NotifStnUser> {
    const observer$ = NotificationClient.ehttp
      .makePut<InotifStnUser>(`${NotifStnUser.baseUrl}/${this._id}`, payload);

    return new NotifStnUser(await lastValueFrom(observer$));
  }
}
